import { useSearchParams } from 'react-router'

import { authClient } from './auth-client'
import { GoogleIcon } from './google-icon'

interface SocialAuthProps {
  /** when set, render the button in the "authenticated" state: same shape,
   *  faded accent fill, disabled, no onClick, "Authenticated with Google" +
   *  checkmark. matches the example's pattern in early-access/step-you.tsx.
   */
  signedInEmail?: string | null
}

export function SocialAuth({ signedInEmail = null }: SocialAuthProps) {
  const [searchParams] = useSearchParams()
  const next = searchParams.get('next')

  const isAuthed = !!signedInEmail

  /** preserve the deep-link intent through oauth: if the user landed on
   *  /login?next=/settings, the google round-trip should end at /settings.
   *  callbackURL must be a full URL because better auth's callback handler
   *  runs at the API host. /signup/finish is the post-auth completion
   *  surface. separate URL from /signup so analytics can distinguish the
   *  two views.
   */
  const callbackURL = next
    ? `${window.location.origin}/signup/finish?next=${encodeURIComponent(next)}`
    : `${window.location.origin}/signup/finish`

  return (
    <div className='flex flex-col gap-4'>
      <button
        type='button'
        disabled={isAuthed}
        onClick={
          isAuthed
            ? undefined
            : () => {
                void authClient.signIn.social({
                  provider: 'google',
                  callbackURL,
                  errorCallbackURL: `${window.location.origin}/login`
                })
              }
        }
        className={`flex h-12 w-full items-center justify-center gap-2.5 rounded-xl border px-4 text-[15px] font-medium transition-colors sm:h-10 sm:text-[14px] ${
          isAuthed
            ? 'cursor-not-allowed border-accent/30 bg-accent-light text-accent dark:text-accent-mid'
            : 'cursor-pointer border-border bg-card text-card-foreground hover:bg-cream/60'
        }`}
      >
        <GoogleIcon />
        {isAuthed ? 'Authenticated with Google' : 'Continue with Google'}
        {isAuthed && (
          <svg
            xmlns='http://www.w3.org/2000/svg'
            viewBox='0 0 20 20'
            fill='currentColor'
            className='size-4'
          >
            <path
              fillRule='evenodd'
              d='M16.707 5.293a1 1 0 0 1 0 1.414l-8 8a1 1 0 0 1-1.414 0l-4-4a1 1 0 1 1 1.414-1.414L8 12.586l7.293-7.293a1 1 0 0 1 1.414 0Z'
              clipRule='evenodd'
            />
          </svg>
        )}
      </button>

      {!isAuthed && (
        <div className='flex items-center gap-3'>
          <div className='h-px flex-1 bg-border' />
          <span className='text-[12px] text-ink-muted'>or</span>
          <div className='h-px flex-1 bg-border' />
        </div>
      )}
    </div>
  )
}
